import type { ReactNode } from 'react';
import { Box, Stack, Typography, Chip, TextField, MenuItem, Select, FormControl } from '@mui/material';
import { useTBContext } from "../../Contexts/useTBStates";
import { TabPanel } from "../TabComponents";
import AddIcon from '@mui/icons-material/Add';
import { EventItemDisplay } from '../EventItemDisplay';
import { HandicapDisplay } from '../Handicap Items/HandicapDisplay';
import { IOSSwitch } from '../../../shared/Components/iosSwitch';
import type { EventDataLayout, RivalryMethod } from '@moment-golf/types';
import { EVENT_LOCKED_FIELDS } from '../Functions/cascadeLockedFields';
import { EventDetailsSectionCard } from './EventDetailsSectionCard';

interface EventDetailsTabProps {
    appDims: any;
    compHeights: any;
    eventDataLayout: EventDataLayout;
    setEventDataLayout: React.Dispatch<React.SetStateAction<EventDataLayout>>;
    eventLocked?: boolean;
}

const REGISTRATION_BLOCK = 2;
const SCORING_METHODS_BLOCK = 3;

export const EventDetailsTab = ({ appDims, compHeights, eventDataLayout, eventLocked }: EventDetailsTabProps) => {

    const { viewEvent, setViewEvent, factor, eventTabValue, appTheme, editMode } = useTBContext();

    const detailItems = eventDataLayout.filter((item) => item.section === "Event Details");
    const maxBlock = Math.max(0, ...detailItems.map((item) => item.block ?? 0));
    // Prefer more columns sooner so each card stays narrower and less prone to overflow
    const columnCount = appDims?.appWidth > 1200 ? 3 : appDims?.appWidth > 750 ? 2 : 1;

    /** Match EventItemDisplay row columns so subsection fields share the same left edge / label width. */
    const subsectionFieldRow = (label: string, value: ReactNode) => (
        <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{
                width: '100%',
                maxWidth: '100%',
                minWidth: 0,
                boxSizing: 'border-box',
                justifyContent: 'flex-start',
            }}
        >
            <Typography
                flex={2}
                textAlign="left"
                sx={{
                    fontWeight: 500,
                    fontSize: 14 * factor,
                    lineHeight: 1.4,
                    minHeight: 24 * factor,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    minWidth: 0,
                    flexShrink: 1,
                }}
            >
                {label}
            </Typography>
            <Box
                sx={{
                    flex: 3,
                    minWidth: 0,
                    maxWidth: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                }}
            >
                {value}
            </Box>
        </Stack>
    );

    const subsectionDividerSx = {
        borderTop: `1px dashed ${appTheme.palette.divider}`,
        pt: 1.5,
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        boxSizing: 'border-box' as const,
    };

    const renderTagsField = (itemIndex: number) => (
        <Box key={`tags-${itemIndex}`} sx={{ width: '100%' }}>
            {subsectionFieldRow(
                'Tags:',
                <Stack
                    direction="row"
                    sx={{
                        flexWrap: 'wrap',
                        gap: '5px',
                        justifyContent: 'flex-start',
                        alignItems: 'flex-start',
                        width: '100%',
                    }}
                >
                    {viewEvent.tags?.map((tag: string, index: number) => (
                        <Chip
                            key={`${viewEvent._id}-tag-${index}`}
                            label={tag}
                            onDelete={editMode ? () => {
                                const newTags = viewEvent.tags.filter((_: string, i: number) => i !== index);
                                setViewEvent({ ...viewEvent, tags: newTags });
                            } : undefined}
                            sx={{
                                height: 18,
                                fontSize: 10,
                                backgroundColor: appTheme.palette.primary.light,
                                color: 'white',
                                '& .MuiChip-deleteIcon': {
                                    fontSize: 12,
                                    color: 'white',
                                },
                                margin: '2px 0',
                            }}
                        />
                    ))}
                    {editMode && (
                        <Chip
                            icon={<AddIcon sx={{ fontSize: 12, color: appTheme.palette.primary.main }} />}
                            label="Add Tag"
                            onClick={() => {
                                const newTag = prompt('Enter new tag:');
                                if (newTag) {
                                    const newTags = [...(viewEvent.tags || []), newTag];
                                    setViewEvent({ ...viewEvent, tags: newTags });
                                }
                            }}
                            sx={{
                                height: 18,
                                fontSize: 10,
                                backgroundColor: appTheme.palette.primary.extraLight,
                                color: appTheme.palette.primary.main,
                                margin: '2px 0',
                            }}
                        />
                    )}
                </Stack>,
            )}
        </Box>
    );

    const colorInputSx = {
        width: 36 * factor,
        height: 36 * factor,
        padding: 0,
        border: `1px solid`,
        borderColor: 'divider',
        borderRadius: 1,
        cursor: editMode ? 'pointer' : 'default',
        opacity: editMode ? 1 : 0.5,
    };

    const renderTeamsSubsection = () => (
        <Stack direction="column" spacing={1.25} sx={subsectionDividerSx}>
            <Typography
                sx={{
                    fontWeight: 600,
                    fontSize: 14 * factor,
                    color: 'text.secondary',
                    textAlign: 'left',
                }}
            >
                Teams
            </Typography>
            {subsectionFieldRow(
                'Team Size:',
                <TextField
                    size="small"
                    type="number"
                    value={viewEvent?.teamSize ?? ''}
                    disabled={!editMode}
                    onChange={(e) => setViewEvent((prev: any) => ({ ...prev, teamSize: Number(e.target.value) }))}
                    sx={{ width: '100%', maxWidth: 160 * factor }}
                    slotProps={{ htmlInput: { min: 1 } }}
                />,
            )}
            {subsectionFieldRow(
                'Counting Scores:',
                <TextField
                    size="small"
                    type="number"
                    value={viewEvent?.countingScores ?? ''}
                    disabled={!editMode}
                    onChange={(e) => setViewEvent((prev: any) => ({ ...prev, countingScores: Number(e.target.value) }))}
                    sx={{ width: '100%', maxWidth: 160 * factor }}
                    slotProps={{ htmlInput: { min: 1 } }}
                />,
            )}
        </Stack>
    );

    const renderRivalrySubsection = () => (
        <Stack direction="column" spacing={1.25} sx={subsectionDividerSx}>
            <Typography
                sx={{
                    fontWeight: 600,
                    fontSize: 14 * factor,
                    color: 'text.secondary',
                    textAlign: 'left',
                }}
            >
                Rivalry
            </Typography>
            {subsectionFieldRow(
                'Scoring Method:',
                <FormControl size="small" sx={{ width: '100%', maxWidth: 200 * factor }} disabled={!editMode}>
                    <Select
                        value={viewEvent?.rivalry?.method ?? 'summation'}
                        onChange={(e) => setViewEvent((prev: any) => ({
                            ...prev,
                            rivalry: { ...(prev.rivalry ?? {}), method: e.target.value as RivalryMethod },
                        }))}
                    >
                        <MenuItem value="summation">Summation</MenuItem>
                    </Select>
                </FormControl>,
            )}
            {subsectionFieldRow(
                'Team A Name:',
                <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%', minWidth: 0 }}>
                    <TextField
                        size="small"
                        value={viewEvent?.rivalry?.teamA?.name ?? ''}
                        disabled={!editMode}
                        placeholder="e.g. East Side"
                        onChange={(e) => setViewEvent((prev: any) => ({
                            ...prev,
                            rivalry: { ...(prev.rivalry ?? {}), teamA: { ...(prev.rivalry?.teamA ?? {}), name: e.target.value } },
                        }))}
                        sx={{ flex: 1, maxWidth: 160 * factor, minWidth: 0 }}
                    />
                    <Box
                        component="input"
                        type="color"
                        disabled={!editMode}
                        value={viewEvent?.rivalry?.teamA?.color ?? '#1976d2'}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setViewEvent((prev: any) => ({
                            ...prev,
                            rivalry: { ...(prev.rivalry ?? {}), teamA: { ...(prev.rivalry?.teamA ?? {}), color: e.target.value } },
                        }))}
                        sx={colorInputSx}
                    />
                </Stack>,
            )}
            {subsectionFieldRow(
                'Team B Name:',
                <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%', minWidth: 0 }}>
                    <TextField
                        size="small"
                        value={viewEvent?.rivalry?.teamB?.name ?? ''}
                        disabled={!editMode}
                        placeholder="e.g. West Side"
                        onChange={(e) => setViewEvent((prev: any) => ({
                            ...prev,
                            rivalry: { ...(prev.rivalry ?? {}), teamB: { ...(prev.rivalry?.teamB ?? {}), name: e.target.value } },
                        }))}
                        sx={{ flex: 1, maxWidth: 160 * factor, minWidth: 0 }}
                    />
                    <Box
                        component="input"
                        type="color"
                        disabled={!editMode}
                        value={viewEvent?.rivalry?.teamB?.color ?? '#e53935'}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setViewEvent((prev: any) => ({
                            ...prev,
                            rivalry: { ...(prev.rivalry ?? {}), teamB: { ...(prev.rivalry?.teamB ?? {}), color: e.target.value } },
                        }))}
                        sx={colorInputSx}
                    />
                </Stack>,
            )}
            {subsectionFieldRow(
                'Rivalry Size:',
                <TextField
                    size="small"
                    type="number"
                    value={viewEvent?.rivalry?.rivalrySize ?? ''}
                    disabled={!editMode}
                    onChange={(e) => setViewEvent((prev: any) => ({
                        ...prev,
                        rivalry: { ...(prev.rivalry ?? {}), rivalrySize: Number(e.target.value) },
                    }))}
                    sx={{ width: '100%', maxWidth: 160 * factor }}
                    slotProps={{ htmlInput: { min: 1 } }}
                />,
            )}
            {subsectionFieldRow(
                'Counting Scores:',
                <TextField
                    size="small"
                    type="number"
                    value={viewEvent?.rivalry?.countingScores ?? ''}
                    disabled={!editMode}
                    onChange={(e) => setViewEvent((prev: any) => ({
                        ...prev,
                        rivalry: { ...(prev.rivalry ?? {}), countingScores: Number(e.target.value) },
                    }))}
                    sx={{ width: '100%', maxWidth: 160 * factor }}
                    slotProps={{ htmlInput: { min: 1 } }}
                />,
            )}
        </Stack>
    );

    const renderSelfSelectionSubsection = () => (
        <Stack direction="column" spacing={1.25} sx={subsectionDividerSx}>
            <Typography
                sx={{
                    fontWeight: 600,
                    fontSize: 14 * factor,
                    color: 'text.secondary',
                    textAlign: 'left',
                }}
            >
                Self-Selection
            </Typography>
            {subsectionFieldRow(
                'Group Self-Selection:',
                <Stack direction="row" spacing={1} alignItems="center">
                    <Typography sx={{ fontSize: 14 * factor, fontWeight: 500 }}>No</Typography>
                    <IOSSwitch
                        checked={!!viewEvent.groupSelfSelection}
                        disabled={!editMode}
                        onChange={(e) => setViewEvent((prev: any) => ({ ...prev, groupSelfSelection: e.target.checked }))}
                    />
                    <Typography sx={{ fontSize: 14 * factor, fontWeight: 500 }}>Yes</Typography>
                </Stack>,
            )}
            {subsectionFieldRow(
                'Tee Self-Selection:',
                <Stack direction="row" spacing={1} alignItems="center">
                    <Typography sx={{ fontSize: 14 * factor, fontWeight: 500 }}>No</Typography>
                    <IOSSwitch
                        checked={!!viewEvent.teeSelfSelection}
                        disabled={!editMode}
                        onChange={(e) => setViewEvent((prev: any) => ({ ...prev, teeSelfSelection: e.target.checked }))}
                    />
                    <Typography sx={{ fontSize: 14 * factor, fontWeight: 500 }}>Yes</Typography>
                </Stack>,
            )}
        </Stack>
    );

    // Fixed column assignment (round-robin) so cards do not reshuffle when edit-mode
    // fields change card height — unlike CSS column masonry, which reflows by height.
    const sectionCards: ReactNode[] = [
        <EventDetailsSectionCard key="core-items" title="Core Items">
            <EventItemDisplay orient="row" label="Event Name" keyName="eventName" flipped={false} />
            <EventItemDisplay
                orient="row"
                label="Course"
                keyName="courseName"
                labelField="courseName"
                optionsKeyName="courses"
                idKeyName="courseID"
                flipped={false}
            />
            <EventItemDisplay orient="row" label="Owner" keyName="ownerName" flipped={false} />
            <EventItemDisplay orient="row" label="Start Date" keyName="startDate" flipped={false} />
            <EventItemDisplay orient="row" label="End Date" keyName="endDate" flipped={false} />
            <EventItemDisplay orient="row" label="Season / Year" keyName="year" flipped={false} />
        </EventDetailsSectionCard>,
        ...[...Array(maxBlock + 1)].map((_, blockIndex) => {
            const blockName = detailItems.find((item) => item.block === blockIndex)?.blockName ?? `Section ${blockIndex}`;
            const blockFields = detailItems.filter((item) => item.block === blockIndex);

            return (
                <EventDetailsSectionCard key={`event-details-block-${blockIndex}`} title={blockName}>
                    {blockFields.map((item, itemIndex: number) => {
                        if (item.field === 'tags') {
                            return renderTagsField(itemIndex);
                        }
                        if (item.blockName === 'Handicap Usage') {
                            return (
                                <HandicapDisplay
                                    key={`hdcp-${itemIndex}`}
                                    level="event"
                                    keyName={item.field}
                                    locked={eventLocked}
                                    orient="row"
                                />
                            );
                        }
                        if (['teamSize', 'countingScores'].includes(item.field)) {
                            return null;
                        }
                        return (
                            <EventItemDisplay
                                key={`${item.field}-${itemIndex}`}
                                orient="row"
                                label={item.label}
                                keyName={item.field}
                                labelField={item.labelField}
                                optionsKeyName={item.optionsKeyName}
                                removable={item.removable || false}
                                locked={item.locked || (eventLocked && EVENT_LOCKED_FIELDS.includes(item.field))}
                                defaultValue={item.defaultValue || null}
                            />
                        );
                    })}

                    {blockIndex === REGISTRATION_BLOCK && viewEvent?.useTeams && renderTeamsSubsection()}
                    {blockIndex === REGISTRATION_BLOCK && viewEvent?.useRivalry && renderRivalrySubsection()}
                    {blockIndex === SCORING_METHODS_BLOCK
                        && viewEvent?.scoreType?.toLowerCase() === 'group'
                        && renderSelfSelectionSubsection()}
                </EventDetailsSectionCard>
            );
        }),
    ];

    const columns: ReactNode[][] = Array.from({ length: columnCount }, () => []);
    sectionCards.forEach((card, index) => {
        columns[index % columnCount].push(card);
    });

    return (
        <TabPanel value={eventTabValue} index={0} appDims={appDims} compHeights={compHeights} factor={factor} fillAvailable>
            <Box
                id="tab-panel-event-details-content"
                sx={{
                    width: '100%',
                    maxWidth: '100%',
                    height: '100%',
                    minHeight: 0,
                    flex: 1,
                    boxSizing: 'border-box',
                    // Room for card shadows at the container / column edges
                    p: 1.5,
                    overflowX: 'hidden',
                    overflowY: 'auto',
                    display: 'grid',
                    gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))`,
                    gap: 2.5,
                    alignItems: 'start',
                }}
            >
                {columns.map((columnCards, columnIndex) => (
                    <Stack
                        key={`event-details-column-${columnIndex}`}
                        direction="column"
                        spacing={0}
                        sx={{ minWidth: 0, maxWidth: '100%' }}
                    >
                        {columnCards}
                    </Stack>
                ))}
            </Box>
        </TabPanel>
    );
};
